import { Chips } from "../Chips";
import { ProductGallery } from "../ProductGallery";
import { ProductOwner } from "../ProductOwner";
import { ReadMore } from "../ReadMore";
import { StrapiProduct } from "~/shared-types";
import { getOfferDetails } from "~/utils/getOfferDetails";
import { Badge } from "../Backroom/Badge";
import { EditMenu } from "./EditMenu";
import { PurchaseInfo } from "./PurchaseInfo";
import { getDeliveryDetails } from "~/utils/getDeliveryDetails";
import { stringToCurrency } from "~/utils/stringConversion";
import Icon from "~/assets/icons/Icon";
import { MarkdownReader } from "../MarkdownReader";
import { FavoriteButton } from "../FavoriteButton";

type Props = {
  product: StrapiProduct;
  isBackroom?: boolean;
};

export const ProductInfo = ({ product, isBackroom }: Props) => {
  const {
    name: productName,
    store: { data: store },
    danishDesign,
    danishProduction,
    danishMaterials,
    description,
    canOnlyBePickedUp,
    isUnique,
  } = product.attributes;

  const offerDetails = getOfferDetails(product);
  const { offersFreeDelivery, freeDeliveryPurchasePrice } =
    getDeliveryDetails(store);

  const declarations = [];
  if (danishDesign) declarations.push("Dansk design");
  if (danishProduction) declarations.push("Dansk produktion");
  if (danishMaterials) declarations.push("Danske materialer");

  return (
    <>
      {isBackroom ? <EditMenu product={product} /> : null}
      <div className="md:grid md:grid-cols-2 items-start xl:px-16 2xl:px-40 mb-20">
        <div>
          <ProductGallery product={product} />
          <div className="flex w-full items-center justify-end -mt-8 md:hidden">
            {isUnique && (
              <Badge className="mr-2" color="unique">
                Unika
              </Badge>
            )}
            <FavoriteButton
              className="-mr-2"
              favorite={{ id: product.id, type: "product" }}
            />
          </div>
        </div>

        <div className="md:pl-7 xl:pl-20">
          <ProductOwner store={store} />

          <h1 className="text-2xl mb-5">{productName}</h1>
          <Chips className="mb-14" texts={declarations} />
          <ReadMore className="mb-14">
            <MarkdownReader className="text-secondary-800">
              {description}
            </MarkdownReader>
          </ReadMore>
          {offerDetails.hasCurrentOffer ? (
            <div className="mb-4">
              <Badge color="yellow">{offerDetails.offerTitle}</Badge>
              <span className="text-secondary-500 text-xs italic">
                Udløber om {offerDetails.daysTillOfferEnds} dag(e)
              </span>
            </div>
          ) : null}
          <PurchaseInfo product={product} isBackroom={isBackroom} />

          {!canOnlyBePickedUp && offersFreeDelivery && (
            <div className="text-secondary-500 text-xs items-center rounded-md bg-secondary-100 p-2 mt-2 inline-block">
              <Icon
                name="shippingSide"
                className="text-warning w-6 h-6 inline mr-2"
              />
              <span>
                Gratis levering ved køb over{" "}
                <span className="font-medium">
                  {stringToCurrency({ amount: freeDeliveryPurchasePrice })}
                </span>
                .
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
